import {
  Button,
  Form,
  FormLayout,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
  TextArea,
  TextField,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { useCanSendTestEvent } from '@/services/permissions';
import { z } from '@customerio/hydra-validation';
import { useZodForm, type BaseFormProps } from '@/components/form';
import { isEmptyString } from '@/utils/string';
import { useSendEvent } from '../hooks';

/**
 * REUSE / PROMOTION NOTE
 *
 * A focused port of Ember's shared `send-event-modal`, scoped to the Activity Logs "Resend" path
 * (a single pre-selected profile + event name + JSON data). Ember's `SendEventModal` is reused
 * app-wide (send test event, forms replay, etc.).
 *
 * When a second consumer needs "send event" in Hydra, promote this to a shared
 * `features/send-event/` module (public `<SendEventModal>` + `useSendEvent`) rather than
 * re-porting it — mirroring how Ember shares its modal. Features to fold in then (present in
 * Ember's version): multi-recipient customer search, the forms/ACE data editors, and event
 * name/data size + disallowed-property validation. (`useSendEvent` already lives in the page
 * `hooks.ts` and moves with it.)
 */

export interface ResendEventModalData {
  environmentId: string;
  /** Internal CIO id of the recipient profile. */
  cioId: string;
  /** Display label for the recipient (name / email / id). */
  recipientLabel: string;
  eventName: string;
  /** Pretty-printed JSON of the event attributes. */
  eventData: string;
  /** Called after a successful send (the page refetches the log list). */
  onSent: () => void;
}

/** Delay before refetching so the sent event has time to process. */
const RESEND_REFETCH_DELAY_MS = 2000;

const RESEND_EVENT_FORM_ID = 'resend-event-form';

/** Valid JSON, or empty (which normalizes to `{}`). */
function isValidJsonOrEmpty(value: string): boolean {
  if (isEmptyString(value)) return true;
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Event name is required; event data must parse as JSON. The `eventData` transform normalizes to a
 * compact JSON string (empty → `{}`) so the output type is the exact payload the API wants — hence
 * distinct input (raw textarea string) and output (normalized JSON) schema types.
 */
const ResendEventFormSchema = z.object({
  name: z.string().trim().min(1, 'Event name is required'),
  eventData: z
    .string()
    .refine(isValidJsonOrEmpty, { message: 'Event data must be valid JSON.' })
    .transform((value) => {
      if (isEmptyString(value)) return '{}';
      try {
        return JSON.stringify(JSON.parse(value));
      } catch {
        return '{}';
      }
    }),
});

type ResendEventFormSchemaInputType = z.input<typeof ResendEventFormSchema>;
type ResendEventFormSchemaOutputType = z.output<typeof ResendEventFormSchema>;

type ResendEventFormProps = BaseFormProps<ResendEventFormSchemaInputType, ResendEventFormSchemaOutputType> & {
  recipientLabel: string;
  /** Cannot send a test event → inputs + send disabled. */
  isReadOnly: boolean;
  modalState: PlumaManagedModalProps<ResendEventModalData>['modalState'];
  onCancel: () => void;
};

/**
 * Form layer: owns the schema, field state, validation, and submission wiring (and the modal
 * chrome so the footer submit button stays with the form it submits). No API behavior lives here —
 * it delegates the validated, normalized payload to `onSubmit`.
 */
function ResendEventForm({
  onSubmit,
  defaultValues,
  recipientLabel,
  isReadOnly,
  modalState,
  onCancel,
}: ResendEventFormProps) {
  const form = useZodForm({ schema: ResendEventFormSchema, defaultValues });

  const handleSubmit = form.handleSubmit(async (values) => {
    if (isReadOnly) return;
    await onSubmit(values, form);
  });

  const isSubmitting = form.formState.isSubmitting;

  return (
    <Modal state={modalState} title="Resend event">
      <ModalBody>
        <Form id={RESEND_EVENT_FORM_ID} onSubmit={handleSubmit}>
          <FormLayout>
            <Text size="sm" color="subtle">
              {/* recipientLabel is the recipient's email/name — end-user PII, keep it out of FullStory. */}
              Sending to <span className="fs-mask">{recipientLabel}</span>
            </Text>
            <TextField label="Event name" isDisabled={isReadOnly} {...form.registerPluma('name')} />
            {/* `fs-mask`: event data can contain customer PII — keep it out of FullStory. */}
            <TextArea
              className="fs-mask"
              label="Event data (JSON)"
              rows={8}
              isDisabled={isReadOnly}
              {...form.registerPluma('eventData')}
            />
          </FormLayout>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onCancel} isDisabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={RESEND_EVENT_FORM_ID}
          variant="primary"
          isDisabled={isReadOnly}
          isLoading={isSubmitting}
        >
          Send
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Action layer (managed modal): owns the send mutation, permission gating, default values, and the
 * success/failure side effects. Composes `ResendEventForm` with the schema-first boundary.
 */
export function ResendEventModal({ data, onClose, modalState }: PlumaManagedModalProps<ResendEventModalData>) {
  const snackbars = useSnackbars();
  const sendEvent = useSendEvent();
  // The Resend control already disables for users who cannot send; this is a defensive backstop.
  const isReadOnly = !useCanSendTestEvent();

  const handleSubmit = async (values: ResendEventFormSchemaOutputType) => {
    try {
      await sendEvent.mutateAsync({
        environmentId: data.environmentId,
        cioId: data.cioId,
        name: values.name,
        data: values.eventData,
      });
    } catch {
      // Keep the modal open with an error snackbar so the user can retry, rather than closing
      // the modal with no feedback on failure.
      snackbars.create({ variant: 'critical', message: 'Could not send event. Try again.' });
      return;
    }
    snackbars.create({ message: `${values.name} event sent.` });
    onClose();
    setTimeout(() => data.onSent(), RESEND_REFETCH_DELAY_MS);
  };

  return (
    <ResendEventForm
      onSubmit={handleSubmit}
      defaultValues={{ name: data.eventName, eventData: data.eventData }}
      recipientLabel={data.recipientLabel}
      isReadOnly={isReadOnly}
      modalState={modalState}
      onCancel={onClose}
    />
  );
}
